<script setup lang="ts">
import { ref, computed, watch, reactive } from 'vue';
import adminAPI from '@/services/api';
import { computedAsync } from '@vueuse/core';
import draggable from 'vuedraggable';
import { formatUTCToLocalTime } from "@/utils/date";

import { PaperClipIcon, InformationCircleIcon, DocumentPlusIcon, DocumentMagnifyingGlassIcon, TrashIcon, CloudIcon, LockOpenIcon }
  from '@heroicons/vue/24/outline';
  import { RadioGroup, RadioGroupLabel, RadioGroupOption, RadioGroupDescription } from '@headlessui/vue'


import BaseButton from '../ui/buttons/BaseButton.vue';
import { useClaimStore, useNotificationStore } from '@/store';
import DotLegalStep from '../ui/badges/DotLegalStep.vue';
import router from '../../router';
import type { ListClaimGroups, SearchClaimCollection, SearchClaimDocument } from "@upclaim/sdk/src/data-contracts";
import { LockClosedIcon } from '@heroicons/vue/24/solid';

defineProps<{
  group: ListClaimGroups[number];
}>()

const updatedGroups = ref({});
const drag = ref(false);
const transitionOptions = computed(() => { return { animation: 200, disabled: false, ghostClass: "ghost", }; });


function handleUpdate({ added, removed }, group) {
  console.log("Group change")
  if (removed) {
    updatedGroups.value[group.id] = true;
  }
  if (added) {
    updatedGroups.value[group.id] = true;
    if (!added.claim) {
      group.claimsGroupsAssociations.push(
        { groupId: group.id, claimId: added.element.id, claim: added.element } as any
      )
      console.log("claimGroups.groups[groupId].claimsGroupsAssociations", group)
    }
    // return e
  }
}

async function saveClaimGroup(group: any) {
  await adminAPI.upsertClaimGroup({
    groupId: group.id,
    groupName: group.groupName,
    claimsIds: group.claimsGroupsAssociations.map(({ claimId }) => claimId).filter(Boolean)
  });
  useNotificationStore().emitNotification({ title: "Groupe modifié avec succès" });
  updatedGroups.value[group.id] = false;
}

</script>
<template>
  <div class="p-2 mt-2">
    <RadioGroup>
        <div class="flex items-center justify-between p-2">
          <h2 class="text-sm font-medium leading-6 text-gray-900">
            <span class="font-normal text-gray-700">Dossiers liés :</span> {{ group.groupName }}
          </h2>
          <div class="inline-flex items-center">
            <a :href="'?claim%5Bquery%5D=' + group.id" target="_blank" class="text-sm font-medium leading-6 text-indigo-600 hover:text-indigo-500">Visualiser</a>
            <span class="mx-2">·</span>
            <button class="w-5 h-5 text-indigo-700">
              <LockOpenIcon v-if="!group.lockedAt" />
              <LockClosedIcon v-else />
            </button>
          </div>
        </div>
        <draggable group="claims"
          v-bind="transitionOptions"
          @start="drag = true"
          @end="drag = false"
          :clone="(group) => ({ ...group.claim })"
          :list="group.claimsGroupsAssociations" item-key="id" role="list"
          class="w-full col-span-3 px-2 py-4 divide-y-8 divide-transparent rounded-md"
          :class="{ 'border bg-white border-gray-300 border-dashed shadow-sm': false }"
        >
          <template #item="{ element, index }: { element: { claim: SearchClaimCollection[number] }, index: number }">
            <div class="text-left" @click="() => router.push({name: 'ClaimOverview', params: {id: element.claim.id}})">
              <RadioGroupOption as="div" :key="index" :value="element.claim" v-slot="{ active, checked }">
              <div :class="[useClaimStore().claim.id === element.claim.id ? 'border-indigo-600 ring-2 ring-indigo-600' : 'border-gray-300', 'relative block cursor-pointer rounded-lg border bg-white px-6 py-4 shadow-sm focus:outline-none sm:flex sm:justify-between']">
                <div>
                  <div class="flex items-center">
                    <div class="flex-shrink-0 w-10 h-10">
                      <img class="w-10 h-10 rounded-full" :src=" element.claim.effectiveAirlineIconURL "
                        alt="Airline Logo" />
                    </div>
                    <div class="ml-4 divide-y-2 divide-transparent">
                      <div class="font-medium text-gray-900">{{ element.claim.effectiveFlightIdentifier }} · {{
                        element.claim.effectiveAirlineName
                        }}</div>
                      <div class="font-medium text-slate-700">
                        Vol {{ element.claim.departureAirportIATA }} → {{ element.claim.arrivalAirportIATA }}
                        (du {{ formatUTCToLocalTime(new Date(element.claim.flightScheduledDepartureUTC), element.claim.departureAirportTimezone, "dd/MM/yyyy") }})
                      </div>
                      <div v-if="element.claim.travelAgencyCompany">
                        <span class="mr-2 text-slate-700">
                          Entreprise : {{
                            // @ts-ignore
                            element.claim.travelAgencyCompany?.displayName
                          }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <span class="flex items-center">
                  <span class="flex flex-col text-sm">
                    <RadioGroupLabel as="span" class="text-xs font-medium text-gray-900">
                      {{ element.claim.passengers[0].firstName }}
                      <span class="uppercase">{{ element.claim.passengers[0].lastName }}</span>
                      <span v-if="element.claim.passengers.length > 1" v-tooltip.right="{
                        html: true,
                        content: element.claim.passengers.filter((_, i) => i)
                          .map(p => `${p.firstName} ${p.lastName.toUpperCase()}`).join('<br>'),
                      }">
                        (+ {{ element.claim.passengers.length - 1 }}pax.)
                      </span>
                    </RadioGroupLabel>
                    <RadioGroupDescription as="span" class="text-gray-500">
                      <span class="inline-flex items-center mt-1 text-xs">
                        <DotLegalStep :legalStepName="element.claim.claimLegalStepName" />
                        <span class="block min-w-full ml-1 sm:inline">{{ element.claim.claimStateDisplayName }}</span>
                      </span>
                      <!-- {{ ' ' }}
                      <span class="hidden sm:mx-1 sm:inline" aria-hidden="true">&middot;</span>
                      {{ ' ' }}
                      <span class="block sm:inline">{{ element.effectiveAirlineName }}</span> -->
                    </RadioGroupDescription>
                  </span>
                </span>
                <RadioGroupDescription as="span" class="flex mt-2 text-sm sm:ml-4 sm:mt-0 sm:flex-col sm:text-right">
                  <span class="font-medium text-gray-900">#{{ element.claim.ticketNumber }}</span>
                  <!-- <span class="ml-1 text-gray-500 sm:ml-0">/mo</span> -->
                </RadioGroupDescription>
                <span :class="[active ? 'border' : 'border-2', checked ? 'border-indigo-600' : 'border-transparent', 'pointer-events-none absolute -inset-px rounded-lg']" aria-hidden="true" />
              </div>
            </RadioGroupOption>
              <!-- <div class="flex items-center flex-1 w-0 cursor-pointer" @click="previewDocument(element.file)">
                <PaperClipIcon class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true" />
                <div class="flex flex-1 min-w-0 gap-2 ml-4">
                  <span class="font-medium truncate">
                    {{ element.documentType.type === "OTHER" ? element.file.name : typeTranslations[element.documentType.type] }}
                  </span>
                </div>
              </div>
              <div class="flex flex-shrink-0 ml-4 space-x-4" v-permission="['UPCLAIM_ADMIN', 'LAWYER']">
                <button type="button" v-tooltip="tooltipFile(element as any)" @click="previewDocument(element.file)"
                  class="font-medium text-blue-600 rounded-md hover:text-blue-500">
                  <DocumentMagnifyingGlassIcon class="w-5 h-5" />
                </button>
                <span class="text-gray-200" aria-hidden="true">|</span>
                <button type="button" class="font-medium text-gray-900 rounded-md hover:text-gray-800"
                  @click="removeClaimDocument(element.id)"
                >
                  <TrashIcon class="w-5 h-5" />
                </button>
              </div> -->
            </div>
          </template>

          <template #footer>
            <div v-if="group">
              <div v-if="group" class="mt-2">
                <BaseButton
                  :text="group.claimsGroupsAssociations?.length ? 'Sauvegarder les modifications' : 'Supprimer le groupe'"
                  type="submit"
                  @click="saveClaimGroup(group)"
                />
              </div>
              <div v-else class="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6">
                <div class="flex items-center flex-1 w-0">
                  <InformationCircleIcon class="flex-shrink-0 w-5 h-5 ml-2 text-gray-400" aria-hidden="true" />
                  <div class="flex flex-1 min-w-0 gap-2 ml-4">
                    <span class="flex items-center font-medium truncate">
                      Glisser une Claim ici pour l'ajouter
                    </span>
                    <div class="ml-auto">
                      <!-- <BaseButton @click="modalAddDocument = true" text="Ajouter" /> -->
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </draggable>
    </RadioGroup>
  </div>
</template>